import { useMemo, useRef, useState } from 'react';
import { Loader2, Search, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { InfiniteScrollSentinel } from '@/lib/pagination/infinite-scroll-sentinel';
import { useCasePnrGroups } from '@/lib/cases/pnr-groups';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { PnrRow } from '@/components/pnr/pnr-roster-row';
import { NotificationAcceptanceFilter } from '@/components/pnr/notification-acceptance-filter';
import { PnrOperationCenter } from '@/routes/disruptions/roster-pnr-drawer';
import { useCaseNotifications } from '@/lib/notifications/use-notifications';
import { matchesAcceptanceFilter } from '@/lib/notifications/labels';
import type { AcceptanceFilter } from '@/lib/notifications/types';
import { useCaseOverview } from '../use-case-overview';
import { useAllocation } from '../allocation-tab/use-allocation';
import { AddPnrsDialog } from './add-pnrs-dialog';

// Passengers tab — now the SAME PNR-grouped roster the disruption control center
// uses (components/pnr/pnr-roster-row · PnrRow), scoped to this case. One row per
// PNR group; expand to see the member passengers with the audited name reveal,
// per-pax needs + operational status; the hotel / voucher / triage badges and the
// rebooking status mirror the disruption roster. Clicking a PNR opens the full
// operation center (flight resolution + care stack). Replaces the old bespoke
// manifest table — friendly labels primary, URNs secondary (frontend-conventions
// §18); the table body is its own bounded scroll region (§18.1, no page scroll).

interface PassengersTabProps {
  caseUrn: string;
}

export function PassengersTab({ caseUrn }: PassengersTabProps) {
  const { t } = useTranslation('caseWork');
  // The "Add PNRs" dialog copy lives with the other case-detail actions.
  const { t: tDetail } = useTranslation('caseDetail');
  const [query, setQuery] = useState('');
  const [gateFilter, setGateFilter] = useState<AcceptanceFilter>('all');
  const [drawerGroup, setDrawerGroup] = useState<DisruptionPnrGroupRow | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // NEX-604: The scrollable container ref is passed to InfiniteScrollSentinel so
  // its IntersectionObserver uses the container as root instead of the viewport.
  // Without this, the sentinel at the bottom of the table can be clipped by the
  // container's overflow and never intersect the viewport-based observer.
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { items, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useCasePnrGroups(caseUrn);
  // Case-level passenger total from the server aggregate (not a count of loaded
  // rows), so the header is honest at scale.
  const overview = useCaseOverview(caseUrn);
  const totalPassengers = overview.data?.passengerCount;
  // NEX-604: Group count from the allocation read (the ONE source of truth for
  // group totals, per architecture_case_detail_numbers_source_of_truth). Using
  // `items.length` would show "50 PNR groups" when there are 77 (one page loaded).
  const allocation = useAllocation(caseUrn);
  const totalGroups = allocation.data?.groups?.total;

  // Per-PNR acceptance/delivery status (N11), WS-driven (no poll). Backs the gate
  // filter here AND every PnrRow's notification cell (React Query dedups → one fetch).
  const { rowFor } = useCaseNotifications(caseUrn);

  // Search narrows the loaded groups client-side over PNR / lead / member names
  // (a single case is bounded — one flight's PNRs); the gate filter then narrows by
  // acceptance state so the operator can work the awaiting / declined queue.
  const visible = useMemo(() => {
    // Defensive: a malformed/empty infinite-list page can flatten to an undefined
    // item — drop falsy rows before rendering (mirrors CasePnrRoster).
    const base = items.filter(Boolean);
    const q = query.trim().toLowerCase();
    const bySearch = !q
      ? base
      : base.filter(
          (g) =>
            g.pnrLabel.toLowerCase().includes(q) ||
            g.leadName.toLowerCase().includes(q) ||
            g.flightLabel.toLowerCase().includes(q) ||
            g.passengers.some((p) => p.fullName.toLowerCase().includes(q)),
        );
    if (gateFilter === 'all') return bySearch;
    return bySearch.filter((g) =>
      matchesAcceptanceFilter(rowFor(g.subCaseUrn)?.acceptanceState ?? 'none', gateFilter),
    );
  }, [items, query, gateFilter, rowFor]);

  // The drawer follows the LIVE row, as on the disruption roster (roster-tab). The click only
  // remembers WHICH group is open; the facts come from the case's pnr-groups, which every
  // in-drawer write invalidates — so a hotel reserved with the drawer open reaches its
  // transport voucher section (#872). The snapshot stays as the fallback while the row is
  // filtered or paged out.
  const liveDrawerGroup = useMemo(
    () =>
      drawerGroup
        ? (items.find((g) => g?.subCaseUrn === drawerGroup.subCaseUrn) ?? drawerGroup)
        : null,
    [items, drawerGroup],
  );

  // Per-bucket tallies over the loaded groups for the filter chips.
  // NEX-604: "All" uses the REAL total from allocation, not just loaded items.
  const gateCounts = useMemo(() => {
    const counts: Partial<Record<AcceptanceFilter, number>> = { all: 0, awaiting: 0, accepted: 0, declined: 0 };
    for (const g of items.filter(Boolean)) {
      const state = rowFor(g.subCaseUrn)?.acceptanceState;
      if (state === 'awaiting' || state === 'accepted' || state === 'declined') {
        counts[state] = (counts[state] ?? 0) + 1;
      }
    }
    // Use the real total from allocation; fall back to loaded count if unavailable.
    counts.all = totalGroups ?? items.length;
    return counts;
  }, [items, rowFor, totalGroups]);

  // "Add PNRs" — pull manifest PNRs into the case that were left out at
  // creation (or excluded and now wanted back). A normal operator action, so no
  // role gate; the dialog mounts only while open, so its addable-pnrs query
  // fires per open. Offered from the header AND the zero-groups empty state — a
  // case whose every PNR was excluded is exactly where the operator adds.
  const addButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setAddOpen(true)}
      className="gap-1.5"
      data-testid="add-pnrs-open"
    >
      <UserPlus className="h-3.5 w-3.5" aria-hidden />
      {tDetail('addPnrs.action')}
    </Button>
  );
  const addDialog = addOpen ? (
    <AddPnrsDialog caseUrn={caseUrn} onClose={() => setAddOpen(false)} />
  ) : null;

  if (isLoading) {
    return (
      <div className="space-y-2 p-4 sm:p-6" data-testid="passengers-loading">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="max-w-3xl">
        <CardContent className="p-6 text-sm text-destructive">
          {t('passengers.loadError')}
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0 && query.trim() === '') {
    return (
      <>
        <EmptyState title={t('passengers.emptyTitle')} action={addButton} />
        {addDialog}
      </>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-4 sm:p-6">
      {/* Search + total header */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-xs">
          <Search
            className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('passengers.searchPlaceholder')}
            aria-label={t('passengers.searchLabel')}
            className="w-full rounded-md border border-input bg-background py-1.5 pl-8 pr-2 text-sm"
          />
        </div>
        <NotificationAcceptanceFilter value={gateFilter} onChange={setGateFilter} counts={gateCounts} />
        <div className="flex items-center gap-3">
          <div className="text-sm text-muted-foreground" data-testid="passenger-stats">
            <span className="font-medium text-foreground">{totalPassengers ?? '—'}</span>{' '}
            {t('passengers.statsPassengers')} ·{' '}
            {/* NEX-604: show the REAL total from allocation, not just loaded items */}
            <span className="font-medium text-foreground">{totalGroups ?? items.length}</span>{' '}
            {t('passengers.statsGroups')}
          </div>
          {addButton}
        </div>
      </div>

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <CardContent
          ref={scrollContainerRef}
          className="min-h-0 flex-1 overflow-y-auto p-0"
          data-testid="passengers-scroll-container"
        >
          <table className="density-table w-full text-sm" data-testid="passenger-roster-table">
            <thead className="sticky top-0 z-10 border-b border-border bg-background text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">{t('passengers.columns.pnr')}</th>
                <th className="px-3 py-2 text-left">{t('passengers.columns.flight')}</th>
                <th className="px-3 py-2 text-left">{t('passengers.columns.priority')}</th>
                <th className="px-3 py-2 text-left">{t('passengers.columns.rebookingOffer')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-sm text-muted-foreground">
                    {gateFilter === 'all'
                      ? t('passengers.noMatch')
                      : t('passengers.noneInBucket', {
                          state: t(`passengers.acceptanceState.${gateFilter}`),
                        })}
                  </td>
                </tr>
              ) : (
                visible.map((group) => (
                  <PnrRow
                    key={group.subCaseUrn}
                    group={group}
                    onOpen={() => setDrawerGroup(group)}
                    showNotifications
                  />
                ))
              )}
            </tbody>
          </table>
          {isFetchingNextPage ? (
            <div
              className="flex items-center justify-center gap-2 p-3 text-xs text-muted-foreground"
              data-testid="passengers-loading-more"
            >
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              {t('shared.loadingMore')}
            </div>
          ) : null}
          <InfiniteScrollSentinel
            onReachEnd={fetchNextPage}
            disabled={!hasNextPage || isFetchingNextPage}
            scrollContainerRef={scrollContainerRef}
          />
        </CardContent>
      </Card>

      <PnrOperationCenter
        scope={{ kind: 'case', caseUrn }}
        group={liveDrawerGroup}
        onClose={() => setDrawerGroup(null)}
      />
      {addDialog}
    </div>
  );
}
